import {Injectable} from '@angular/core';
import {ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Observable} from "rxjs";
import {UserRoles} from "../user/user-roles";
import {RouteData} from "../shared/services/route-data/route-data";
import * as fromStore from '../store';
import {first, switchMap} from "rxjs/operators";
import {DbStoreState} from '../store';
import {Store} from '@ngrx/store';
import {UserService} from '../user/user.service';
import {UserSubscriptionCategory, UserSubscriptionType} from "../user/user-subscription";
import {ExpertsService} from "../services/API/experts.service";
import {TypeUtils} from "../shared/utils/type-utils";
import {combineLatest} from "rxjs";
import {DbModalService} from "../modules/modal/modal";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";

@Injectable()
export class AuthGuard implements CanActivate {

  private subscriptionId: number;
  private pluginSlug: string;
  private pluginId: number;


  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<DbStoreState>,
    private _userService: UserService,
    private _dbModalService: DbModalService,
    private _expertsService: ExpertsService,
    private readonly _googleAnalyticsService: GoogleAnalyticsService,
  ) {
  }

  // TODO check all cases in auth guard (check variable names, functions, statements) handle and remove zendesk part
  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return new Promise<boolean>((resolve) => {
      this._store.select(fromStore.getUserLoaded)
        .pipe(
          first((loaded: boolean) => loaded),
          switchMap(() => combineLatest(
              this._store.select(fromStore.getUser),
              this._store.select(fromStore.getUserSubscription)
            )
          ),
          first()
        )
        .subscribe(([user, userSubscription]) => {
          const role: string = user ? UserRoles.user : UserRoles.guest;
          const routeData: RouteData = new RouteData(next.data);
          const isAuthorized: boolean = routeData.roles.indexOf(role) !== -1 || routeData.roles.indexOf(UserRoles.all) !== -1;
          const isCheckout: boolean = ((routeData.data.pageId === "checkout" || routeData.data.pageId === "checkout-ai" || routeData.data.pageId === "start-ai-website-building")
            && role !== UserRoles.guest) ? ((userSubscription && userSubscription.trialBoosterClient) || (user && (user.planType === UserSubscriptionCategory.STARTER || user.planType === UserSubscriptionCategory.REGISTERED))) : true;
          const isSignUp: boolean = ((routeData.data.pageId === "sign-up" || routeData.data.pageId === "start-ai-website-building" || routeData.data.pageId === "v2-ai-website-building") && role !== UserRoles.guest);
          const isSetup: boolean = ((routeData.data.pageId === "setup-onboarding" || routeData.data.pageId === "start-ai-website-building" || routeData.data.pageId === "v2-ai-website-building")   && role === UserRoles.guest);
          const queryParams: any = next.queryParams;
          const apiIsNotAccessible = userSubscription?.type === UserSubscriptionCategory?.REGISTERED;
          if (routeData.data.pageId === 'account-deleted') {
            if (isAuthorized) {
              this._router.navigate(['/websites']);
              return;
            }
            return resolve(true);
          }
          if (routeData.data.pageId === '10web-api' && apiIsNotAccessible) {
            this._router.navigate(['/websites']);
            return;
          }
          if (queryParams.two_comes_from) {
            this._sendComesFromEvent(next.queryParams['two_comes_from'], user);
          }
          if (queryParams) {
            this.subscriptionId = parseInt(queryParams.subscr_id, 10);
            this.pluginSlug = queryParams.plugin_slug;
            this.pluginId = queryParams.plugin_id ? parseInt(queryParams.plugin_id, 10) : null;

            if (!!user && !!this.subscriptionId && routeData?.data?.pageId === "checkout") {
              this._store.select(fromStore.getUserAvailableSubscription(this.subscriptionId))
                .subscribe(userAvailableSubscription => {
                  if (!!TypeUtils.isNull(userAvailableSubscription)) {
                    return this._router.navigate([`/upgrade-plan`]);
                  }
                });
            }

          }

          if (queryParams.finalize && routeData && routeData.data && routeData.data.pageId && (routeData.data.pageId === "start-ai-website-building" || routeData.data.pageId === "v2-ai-website-building")) {
            return resolve(true);
          }

          if (isAuthorized && isCheckout && user && TypeUtils.isNull(userSubscription)) {
            this._router.navigate(['/something-went-wrong'], {skipLocationChange: true, replaceUrl: true});
          }

          if (user && !queryParams.finalize) {
            const params = {
              fromExperts: queryParams.fromExperts || new URL(window.location.href).searchParams.get('fromExperts'),
              plugin_id: queryParams.plugin_id || new URL(window.location.href).searchParams.get('plugin_id'),
            };
            this.expertsRedirectionHandler(params);
          }

          // Check for guest without prefilledAiData on AI builder routes
/*          const isV2AiWebsiteBuilding = state.url.includes('/v2-ai-website-building');
          const isStartAiWebsiteBuilding = state.url.includes('/start-ai-website-building');
          const isAiBuilderRoute = isV2AiWebsiteBuilding || isStartAiWebsiteBuilding;
          const hasPrefilledDataInStorage = !!localStorage.getItem('prefilledAiData');
          const hasPrefilledDataInQuery = !!queryParams.prefilledAiData;

          if (!user && isAiBuilderRoute && !hasPrefilledDataInStorage && !hasPrefilledDataInQuery) {
            this._router.navigate(['/sign-up']);
            return resolve(false);
          }*/

          if (isAuthorized && (isCheckout || isSignUp || isSetup)) {
            return resolve(true);
          }

          if (user && routeData.data.pageId === "setup-onboarding") {
            if (queryParams.emailCampaign || new URL(window.location.href).searchParams.get("emailCampaign")) {
              this._router.navigate(['/setup/builder-v2'], {queryParams: {emailCampaign: 1}});
            }
            else if (!localStorage.getItem('prefilledAiData')) {
              this._router.navigate(['/']);
            }
            return resolve(true);
          }
          if (role === UserRoles.guest && !isSetup) {
            const navigationQueryParams = {
              returnUrl: state.url
            };
            const invitationData = queryParams.invitation_data || new URL(window.location.href).searchParams.get("invitation_data");
            if (invitationData) {
              navigationQueryParams['invitation_data'] = invitationData;
            }
            this._router.navigate(['/login'], {queryParams: navigationQueryParams});
          } else if (user && !userSubscription?.trialClient) {
            const route = user.planType === UserSubscriptionType.PLATFORM ||
            (user.planType === UserSubscriptionType.ONE_PRODUCT && this.subscriptionId)
              ? '/upgrade-plan'
              : '/websites';
            const queryParams = user.planType === UserSubscriptionType.ONE_PRODUCT && this.subscriptionId
              ? { active_tab: 'bundle' }
              : {};
            this._router.navigate([route], { queryParams });
          } else {
            if (this.subscriptionId && user && !userSubscription?.trialClient && user.planType !== UserSubscriptionCategory.REGISTERED) {
              this._router.navigate(['/upgrade-plan'], {
                queryParams: {
                  subscr_id: this.subscriptionId,
                  plugin_slug: this.pluginSlug
                }
              });
            } else if (routeData.data.pageId !== "login" && (userSubscription?.trialClient || user.planType === UserSubscriptionCategory.REGISTERED)) {
              resolve(true);
              return;
            } else {
              if (user && queryParams && queryParams.db_success && queryParams.email && queryParams.token && state.url.indexOf('/login') !== -1) {
                this._router.navigate(['/websites'], {
                  queryParams: {
                    db_success: queryParams.db_success,
                    email: queryParams.email,
                    token: queryParams.token
                  }
                });
              } else {
                this._router.navigate(['/websites']);
              }
            }
          }

          resolve(false);
        });
    });
  }

  private expertsRedirectionHandler(queryParams): void {
    if (queryParams.fromExperts) {
      this._expertsService.redirectToExperts(queryParams.fromExperts);
    } else if (queryParams.plugin_id === '200') {
      this._expertsService.redirectToExperts();
    }
  }


  private _sendComesFromEvent(label: string, user): void {
    if (user) {
      const analyticsData = {
        eventCategory: 'Dashboard Pageview',
        eventAction: 'From Booster Plugin',
        eventLabel: label,
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }

}
